import { useState } from "react";
import { useSeo } from "@/lib/seo";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? String(iso).slice(0, 10) : "—");

export function JobsRequest() {
  useSeo({ title: "My jobs — all your reports, re-runs and credits", description: "Enter the email you paid with to get a private link to every 99tests job, report, re-run and your credit balance.", path: "/jobs", noindex: true });
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try { await api.historyRequest(email); setSent(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  }
  return (
    <main className="app-main"><div className="wrap narrow" data-testid="jobs-request">
      <span className="pill neutral">Your jobs</span>
      <h1 style={{ marginTop: 12 }}>All your reports, re-runs and credits.</h1>
      <p className="lede">No account needed. Enter the email you paid with and we'll send you a private link to every job you've run with 99tests.</p>
      {sent ? (
        <div className="panel-soft" style={{ padding: 26 }} data-testid="jobs-request-sent">
          <h3>Check your inbox</h3>
          <p className="muted">If <b>{email}</b> has run jobs with us, a link is on its way. It works for 7 days.</p>
        </div>
      ) : (
        <form className="panel-soft" style={{ padding: 26 }} onSubmit={submit}>
          <div className="field"><label htmlFor="he">Email used at checkout</label><input id="he" type="email" required data-testid="history-email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <button className="btn blue block" type="submit" disabled={busy} data-testid="history-submit">{busy ? "Sending…" : "Email me my jobs link"}</button>
        </form>
      )}
    </div></main>
  );
}

export default function JobsHistory() {
  useSeo({ title: "Your jobs", noindex: true });
  const { token } = useParams();
  const { data, isLoading, error } = useQuery({ queryKey: ["history", token], queryFn: () => api.history(token), retry: false });
  if (isLoading) return <main className="app-main"><div className="wrap narrow"><p>Loading…</p></div></main>;
  if (error) return <main className="app-main"><div className="wrap narrow" data-testid="history-expired"><h1>Link expired</h1><p className="lede">{error?.response?.data?.detail || "This link is no longer valid."} <Link to="/jobs">Request a new one</Link>.</p></div></main>;
  const credits = Object.entries(data.credits).filter(([, v]) => v > 0);
  return (
    <main className="app-main"><div className="wrap" data-testid="history-page">
      <span className="pill neutral">{data.email}</span>
      <h1 style={{ marginTop: 12 }}>Your jobs <span className="muted" style={{ fontSize: 22 }}>({data.jobs.length})</span></h1>
      <div className="panel-soft" style={{ padding: "18px 22px", marginBottom: 18, display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }} data-testid="credit-balance">
        <div><div className="muted small">Credit balance</div>
          <div style={{ fontFamily: "var(--display)", fontWeight: 800, fontSize: 28 }}>{credits.length ? credits.map(([c, v]) => money(v, c)).join(" · ") : money(0, "INR")}</div></div>
        <p className="muted small" style={{ margin: 0, maxWidth: "60ch" }}>Sessions we couldn't fill are credited back here instead of refunded, and applied automatically to your next re-run with this email.</p>
      </div>
      <div className="panel-soft" style={{ overflow: "auto" }}>
        <table className="tbl"><thead><tr><th>Job</th><th>App</th><th>Package</th><th>State</th><th>Sessions</th><th className="c">Paid</th><th>Created</th><th></th></tr></thead>
          <tbody>{data.jobs.map((j) => (
            <tr key={j.id} data-testid={`history-job-${j.code}`}>
              <td><b>{j.code}</b>{j.rerun_of && <div className="muted small">re-run of {j.rerun_of}</div>}</td>
              <td>{j.app_label || "—"}<div className="muted small" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.app_url}</div></td>
              <td>{j.package}</td>
              <td><span className={`pill ${j.state === "delivered" ? "" : "neutral"}`}>{j.state}</span>{j.credit_minor > 0 && <div className="muted small">{money(j.credit_minor, j.currency)} credited</div>}</td>
              <td>{j.sessions_done}/{j.sessions_total}{j.findings != null && <div className="muted small">{j.findings} findings</div>}</td>
              <td className="c num">{money(j.amount_minor - (j.credit_used_minor || 0), j.currency)}{j.credit_used_minor > 0 && <div className="muted small">−{money(j.credit_used_minor, j.currency)} credit</div>}</td>
              <td className="small">{fmt(j.created_at)}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {j.status_token && <a className="btn sm ghost" href={`/status/${j.status_token}`} data-testid={`history-status-${j.code}`}>Status</a>}{" "}
                {j.report_available && <a className="btn sm" href={`/report/${j.status_token}`} data-testid={`history-report-${j.code}`}>Report</a>}
              </td>
            </tr>
          ))}</tbody></table>
        {data.jobs.length === 0 && <div className="empty"><b>No jobs yet</b><Link to="/#packages">Start one</Link>.</div>}
      </div>
      {data.ledger.length > 0 && (
        <details style={{ marginTop: 18 }}><summary className="muted small" style={{ cursor: "pointer" }}>Credit history ({data.ledger.length})</summary>
          <div className="panel-soft" style={{ overflow: "auto", marginTop: 8 }}>
            <table className="tbl"><thead><tr><th>When</th><th>Job</th><th>Reason</th><th className="c">Amount</th></tr></thead>
              <tbody>{data.ledger.map((e) => <tr key={e.id}><td className="small">{fmt(e.at)}</td><td>{e.job_code || "—"}</td><td>{e.reason}</td><td className="c num" style={{ color: e.amount_minor < 0 ? "var(--fail)" : "var(--green)" }}>{e.amount_minor < 0 ? "−" : "+"}{money(Math.abs(e.amount_minor), e.currency)}</td></tr>)}</tbody></table>
          </div>
        </details>
      )}
    </div></main>
  );
}
